/** Thin JSON client for the khata API. Same-origin, cookie session. */

export interface ApiErrorBody {
  readonly code: string;
  readonly message: string;
  readonly field?: string;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message);
  }
  get code(): string {
    return this.body.code;
  }
  get field(): string | undefined {
    return this.body.field;
  }
}

/** Thrown when the network is unreachable (not an HTTP error). */
export class OfflineError extends Error {
  override readonly name = 'OfflineError';
  constructor() {
    super('No connection to the server.');
  }
}

export type Unauthorized = (reason: 'login' | 'setup') => void;
let onUnauthorized: Unauthorized = () => {};
export function setUnauthorizedHandler(fn: Unauthorized): void {
  onUnauthorized = fn;
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch {
    throw new OfflineError();
  }
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const err = (json as { error?: ApiErrorBody } | null)?.error ?? {
      code: 'http_' + res.status,
      message: `Server error (${res.status}).`,
    };
    if (res.status === 401) onUnauthorized(err.code === 'not_setup' ? 'setup' : 'login');
    throw new ApiError(res.status, err);
  }
  return json as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
